import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import {
  findDealItemByVersionOrItemId,
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import { productsMapState, productsState } from '@/states/products.state';
import { getDefaultStore, useAtomValue } from 'jotai';

/**
 * Toggles the "TVA sur marge" flag of a deal item, optimistically.
 *
 * Extracted from the table so any surface offering the VAT cell (the table
 * column, the VAT review dialog) drives it through the exact same mutation and
 * rollback path instead of reimplementing it.
 */
export const useToggleVatMargin = (dealId: string) => {
  const { t } = useTranslation();
  const products = useAtomValue(productsState);
  const { updateProducts } = useUpdateDealItems(dealId);
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  const toggleImpl = async (id: string) => {
    const product = findDealItemByVersionOrItemId(products, id);
    if (!product) return;
    const productsMap = getDefaultStore().get(productsMapState);
    if (
      isDealItemCategoryUpdating(resolveDealItemFromMap(productsMap, product))
    )
      return;

    const newHasVatMargin = !product.activeVersion.hasVatMargin;
    const payload = [{ id, hasVatMargin: newHasVatMargin }];

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) => {
          if (id !== p.id) return p;
          return {
            ...p,
            activeVersion: {
              ...p.activeVersion,
              hasVatMargin: newHasVatMargin,
            },
          };
        }),
      mutation: () => updateProducts(payload),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2?.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2?.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'handleVatMargin',
      itemIds: [id],
    });
  };

  // Kept behind a ref so the identity stays stable across renders: the table
  // memoizes its column definitions on it.
  const toggleRef = useRef(toggleImpl);
  toggleRef.current = toggleImpl;

  return useCallback(async (id: string) => toggleRef.current(id), []);
};
